/**
 * Google OAuth popup flow using implicit grant (id_token).
 * Opens Google OAuth in a popup, user signs in, Google redirects back
 * with an id_token in the URL fragment. We extract it and return it.
 */

const GOOGLE_AUTH_URL = "https://accounts.google.com/o/oauth2/v2/auth";

/**
 * Opens a Google OAuth popup and returns the id_token.
 * Uses response_type=id_token (implicit flow) so we get the token directly
 * without needing a server-side code exchange.
 */
export function signInWithGoogle(clientId: string, redirectUri: string): Promise<string> {
    return new Promise((resolve, reject) => {
        const nonce = Math.random().toString(36).substring(2) + Date.now().toString(36);
        const params = new URLSearchParams({
            client_id: clientId,
            redirect_uri: redirectUri,
            response_type: "id_token",
            scope: "email profile openid",
            nonce,
            prompt: "select_account",
        });
        const authUrl = `${GOOGLE_AUTH_URL}?${params.toString()}`;

        // Open popup
        const width = 500;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        const popup = window.open(
            authUrl,
            "google-signin",
            `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`,
        );

        if (!popup) {
            reject(new Error("Popup blocked by browser"));
            return;
        }

        const pollInterval = setInterval(() => {
            try {
                if (popup.closed) {
                    clearInterval(pollInterval);
                    reject(new Error("Google sign-in was cancelled"));
                    return;
                }

                // Check if popup navigated back to our redirect URI
                // The id_token will be in the URL fragment (#id_token=...)
                const popupUrl = popup.location.href;
                if (popupUrl && popupUrl.startsWith(redirectUri)) {
                    clearInterval(pollInterval);

                    // Parse fragment parameters
                    const hash = popup.location.hash.substring(1); // remove #
                    const fragmentParams = new URLSearchParams(hash);
                    const idToken = fragmentParams.get("id_token");
                    const error = fragmentParams.get("error");

                    popup.close();

                    if (error) {
                        reject(new Error(`Google OAuth error: ${error}`));
                        return;
                    }

                    if (idToken) {
                        resolve(idToken);
                    } else {
                        reject(new Error("No id_token received from Google"));
                    }
                }
            } catch (e) {
                // Cross-origin error — popup still on Google's domain
            }
        }, 200);
    });
}

// No-op — popup flow doesn't need script preloading
export async function initGoogle(_clientId: string): Promise<void> {}
